'use client';

import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, type MotionValue } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { fadeIn, fadeUp, scaleIn, staggerFast, viewportLazy } from '@/lib/motion';
import { useCalendly } from '@/components/CalendlyPopup';
import { usePerf } from '@/contexts/PerformanceContext';

function CalendlyButton() {
  const { open } = useCalendly();
  return (
    <motion.button
      type="button"
      onClick={open}
      className="btn-glow inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold text-sm relative z-10 cursor-pointer"
      whileHover={{ scale: 1.04, y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="relative z-10">Book a Free Consultation</span>
      <svg
        className="w-4 h-4 relative z-10"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </motion.button>
  );
}

const steps = [
  {
    num: '01',
    icon: 'MagnifyingGlassIcon',
    title: 'Discover',
    description:
      'We deep-dive into your vision, goals, and users. Requirements, research, and strategy alignment.',
    color: '#7C8CFF',
    bgColor: 'rgba(124, 140, 255, 0.12)',
  },
  {
    num: '02',
    icon: 'PencilSquareIcon',
    title: 'Design',
    description:
      'Wireframes, prototypes, and pixel-perfect UI. Validated with real users before a line of code is written.',
    color: '#7C3AED',
    bgColor: 'rgba(124, 58, 237, 0.12)',
  },
  {
    num: '03',
    icon: 'CodeBracketIcon',
    title: 'Develop',
    description:
      'Agile sprints, clean architecture, and rigorous code reviews. We build with the future in mind.',
    color: '#0891B2',
    bgColor: 'rgba(8, 145, 178, 0.12)',
  },
  {
    num: '04',
    icon: 'CloudArrowUpIcon',
    title: 'Deploy',
    description:
      'CI/CD pipelines, zero-downtime deployments, and post-launch monitoring. Ship with confidence.',
    color: '#059669',
    bgColor: 'rgba(5, 150, 105, 0.12)',
  },
  {
    num: '05',
    icon: 'ArrowTrendingUpIcon',
    title: 'Scale',
    description:
      'Ongoing support, performance optimization, and feature iteration. We grow with your product.',
    color: '#2563FF',
    bgColor: 'rgba(37, 99, 255, 0.12)',
  },
];

const MUTED = '#9CA3AF';
const MUTED_BG = 'rgba(156, 163, 175, 0.12)';
const MUTED_BORDER = 'rgba(156, 163, 175, 0.35)';
const LAST = steps.length - 1;

type Step = (typeof steps)[number];

function reached(value: number, threshold: number) {
  return value >= threshold - 0.001;
}

function StepColumn({
  step,
  index,
  progress,
}: {
  step: Step;
  index: number;
  progress: MotionValue<number>;
}) {
  const threshold = index / LAST;
  const color = useTransform(progress, (value) => (reached(value, threshold) ? step.color : MUTED));
  const background = useTransform(progress, (value) =>
    reached(value, threshold) ? step.bgColor : MUTED_BG
  );
  const borderColor = useTransform(progress, (value) =>
    reached(value, threshold) ? `${step.color}30` : MUTED_BORDER
  );
  const badgeText = useTransform(progress, (value) =>
    reached(value, threshold) ? '#FFFFFF' : '#0F1117'
  );
  const description = useTransform(progress, (value) =>
    reached(value, threshold) ? 'rgba(229, 231, 235, 0.7)' : MUTED
  );

  return (
    <motion.div className="group flex w-full flex-col items-center text-center md:min-w-0 md:flex-1" variants={scaleIn}>
      <motion.div
        className="relative mb-4 flex h-16 w-16 items-center justify-center rounded-full border md:mb-5"
        style={{ background, borderColor, color }}
        whileHover={{ scale: 1.12, y: -4, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } }}
      >
        <Icon name={step.icon as 'MagnifyingGlassIcon'} size={24} className="h-6 w-6" />
        <motion.div
          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full text-[0.6rem] font-bold"
          style={{ background: color, color: badgeText }}
        >
          {step.num}
        </motion.div>
      </motion.div>
      <motion.h3 className="mb-2 text-base font-bold" style={{ color }}>
        {step.title}
      </motion.h3>
      <motion.p className="max-w-xs text-sm leading-relaxed md:max-w-[140px] md:text-xs" style={{ color: description }}>
        {step.description}
      </motion.p>
    </motion.div>
  );
}

function Connector({ index, progress }: { index: number; progress: MotionValue<number> }) {
  const start = index / LAST;
  const end = (index + 1) / LAST;
  const scaleX = useTransform(progress, [start, end], [0, 1]);
  const next = steps[index + 1];
  const arrowColor = useTransform(progress, (value) => (reached(value, end) ? next.color : MUTED));

  const scaleY = useTransform(progress, [start, end], [0, 1]);

  return (
    <motion.div
      className="flex flex-shrink-0 flex-col items-center py-3 md:mt-8 md:flex-row md:px-1 md:py-0"
      variants={fadeIn}
      aria-hidden="true"
    >
      <div className="relative h-8 w-px md:h-px md:w-12" style={{ background: 'rgba(156, 163, 175, 0.45)' }}>
        <motion.div
          className="absolute inset-0 origin-top md:hidden"
          style={{
            scaleY,
            background: `linear-gradient(180deg, ${steps[index].color}99, ${next.color}99)`,
          }}
        />
        <motion.div
          className="absolute inset-0 hidden origin-left md:block"
          style={{
            scaleX,
            background: `linear-gradient(90deg, ${steps[index].color}99, ${next.color}99)`,
          }}
        />
      </div>
      <motion.svg
        className="mt-0.5 h-3 w-3 flex-shrink-0 rotate-90 md:-ml-1 md:mt-0 md:rotate-0"
        viewBox="0 0 24 24"
        fill="none"
        style={{ color: arrowColor, opacity: 0.6 }}
        aria-hidden="true"
      >
        <path
          d="M5 12h14M13 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.svg>
    </motion.div>
  );
}

export default function ProcessSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const { reducedMotion } = usePerf();
  const reducedRef = useRef(reducedMotion);
  reducedRef.current = reducedMotion;
  const progress = useMotionValue(reducedMotion ? 1 : 0);
  const ctaOpacity = useMotionValue(reducedMotion ? 1 : 0);
  const ctaY = useMotionValue(reducedMotion ? 0 : 16);
  const ctaPointer = useTransform(ctaOpacity, (value) => (value > 0.45 ? 'auto' : 'none'));

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let ctaFrame = 0;
    let ctaShown = reducedRef.current;
    let primed = false;

    const fadeCta = (show: boolean, immediate: boolean) => {
      ctaShown = show;
      const toOpacity = show ? 1 : 0;
      const toY = show ? 0 : 16;
      cancelAnimationFrame(ctaFrame);
      if (immediate || reducedRef.current) {
        ctaOpacity.set(toOpacity);
        ctaY.set(toY);
        return;
      }
      const fromOpacity = ctaOpacity.get();
      const fromY = ctaY.get();
      const started = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - started) / 460);
        const eased = 1 - (1 - t) * (1 - t);
        ctaOpacity.set(fromOpacity + (toOpacity - fromOpacity) * eased);
        ctaY.set(fromY + (toY - fromY) * eased);
        if (t < 1) ctaFrame = requestAnimationFrame(tick);
      };
      ctaFrame = requestAnimationFrame(tick);
    };

    const update = () => {
      if (reducedRef.current || track.getBoundingClientRect().width === 0) {
        progress.set(1);
        fadeCta(true, true);
        return;
      }

      const rect = track.getBoundingClientRect();
      const narrow = window.matchMedia('(max-width: 767px)').matches;
      const start = window.innerHeight * (narrow ? 0.82 : 0.78);
      const end = narrow ? window.innerHeight * 0.42 - rect.height : window.innerHeight * 0.3;
      const next = Math.min(1, Math.max(0, (start - rect.top) / (start - end || 1)));
      progress.set(next);

      const complete = next >= 0.999;
      if (!primed) {
        primed = true;
        fadeCta(complete, true);
        return;
      }
      if (complete !== ctaShown) fadeCta(complete, false);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    return () => {
      cancelAnimationFrame(ctaFrame);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ctaOpacity, ctaY, progress, reducedMotion]);

  return (
    <section
      id="process"
      className="relative overflow-hidden bg-[#0F1117] py-14 text-white lg:py-24"
    >
      <div id="how-we-build" className="scroll-mt-28" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] orb orb-blue opacity-10 pointer-events-none"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 w-full">
        <motion.div
          className="mb-10 lg:mb-14 text-center"
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={viewportLazy}
        >
          <span className="section-label block mb-3">How We Work</span>
          <h2 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl">
            From idea to
            <span className="gradient-text"> launch.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-lg text-[#E5E7EB]/80">
            A clear, collaborative workflow that keeps every decision grounded in user needs,
            product goals, and long-term scalability.
          </p>
        </motion.div>

        <motion.div
          ref={trackRef}
          data-process-track
          className="flex flex-col items-center gap-0 md:flex-row md:items-start"
          initial="hidden"
          whileInView="visible"
          variants={staggerFast}
          viewport={viewportLazy}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.num}>
              <StepColumn step={step} index={index} progress={progress} />
              {index < LAST && <Connector index={index} progress={progress} />}
            </React.Fragment>
          ))}
        </motion.div>

        <motion.div
          className="mt-12 rounded-2xl border border-white/10 bg-[#1D2129] p-8 text-center lg:mt-16"
          style={{ opacity: ctaOpacity, y: ctaY, pointerEvents: ctaPointer }}
          whileHover={{ y: -4, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } }}
        >
          <p className="mb-2 text-sm text-[#E5E7EB]/70">Ready to start your project?</p>
          <h3 className="mb-6 text-2xl font-extrabold text-white md:text-3xl">
            Let&apos;s kick off your <span className="gradient-text">discovery call.</span>
          </h3>
          <CalendlyButton />
        </motion.div>
      </div>
    </section>
  );
}
